import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import { Session, SubscriberSessionPayload } from "../../../types/workout";

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  result?: T;
  results?: T;
  data?: T;
}

const toSeconds = (v: number | string): number => {
  if (typeof v === "number") return v;
  const parts = v.split(":").map((n) => Number(n));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) {
    const [h, m, s] = parts;
    return h * 3600 + m * 60 + s;
  }
  if (parts.length === 2) {
    const [m, s] = parts;
    return m * 60 + s;
  }
  return Number(v) || 0;
};

export const workoutSessionApi = createApi({
  reducerPath: "workoutSessionApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    "Session",
    "Sessions",
    "SessionFormats",
    "SessionFormatBlocks",
    "SessionFormatGroupBlocks",
    "Exercise",
    "SessionGroupBlocks",
  ],

  endpoints: (builder) => ({
    getSessions: builder.query<
      any,
      { status?: string; name?: string; page?: number; limit?: number } | void
    >({
      query: (params) => {
        let url = `${ApiEndpoints.getAllSessions}`;
        const query: string[] = [];
        if (params?.status)
          query.push(`status=${encodeURIComponent(params.status)}`);
        if (params?.name)
          query.push(`name=${encodeURIComponent(params.name)}`);
        if (params?.page)
          query.push(`page=${params.page}`);
        if (params?.limit)
          query.push(`limit=${params.limit}`);
        if (query.length) url += `?${query.join("&")}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: (result: any) => {
        const items = result?.results || [];
        return Array.isArray(items)
          ? [
            ...items.map((item: any) => ({
              type: "Session" as const,
              id: item._id || item.id,
            })),
            { type: "Session", id: "LIST" },
          ]
          : [{ type: "Session", id: "LIST" }];
      },
    }),

    searchSessions: builder.query<any, string>({
      query: (name) => ({
        url: `${ApiEndpoints.getAllSessions}?name=${encodeURIComponent(name)}`,
        method: "GET",
      }),
      providesTags: (result: any) => {
        const items = result?.results || [];
        return Array.isArray(items)
          ? [
            ...items.map((item: any) => ({
              type: "Session" as const,
              id: item._id || item.id,
            })),
            { type: "Session", id: "LIST" },
          ]
          : [{ type: "Session", id: "LIST" }];
      },
    }),

    getSessionById: builder.query<any, string>({
      query: (sessionid) => ({
        url: `${ApiEndpoints.getSessionById}/${sessionid}`,
        method: "GET",
      }),
      providesTags: (_result, _error, sessionid) => [
        { type: "Session", id: sessionid },
      ],
    }),

    // UPDATED: create subscriber session with correct payload and time conversion
    createSession: builder.mutation<ApiResponse<any>, SubscriberSessionPayload>(
      {
        query: (data) => {
          const body = {
            name: data.name,
            category: data.category, // changed from data.type
            warmupTime: toSeconds(data.warmupTime),
            cooldownTime: toSeconds(data.cooldownTime),
            duration: data.duration,
            difficultyLevels: data.difficultyLevels,
            formats: data.formats,
            subscriber: data.subscriber,
          };
          return {
            url: `${ApiEndpoints.createSession}`,
            method: "POST",
            body,
          };
        },
        invalidatesTags: [{ type: "Session", id: "LIST" }],
      }
    ),
    duplicateSession: builder.mutation<
      ApiResponse<any>,
      { sessionid: string }
    >({
      query: ({ sessionid }) => ({
        url: `${ApiEndpoints.duplicateSession}/${sessionid}/duplicate`,
        method: "POST",
      }),
      extraOptions: { hideToast: true },
      invalidatesTags: [{ type: "Session", id: "LIST" }],
    }),

    // Edit session (was editExercise)
    editSession: builder.mutation<
      ApiResponse<Session>,
      { id: string } & Partial<SubscriberSessionPayload>
    >({
      query: ({ id, ...data }) => {
        return {
          url: `${ApiEndpoints.editSession}/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Session", id },
        { type: "Session", id: "LIST" },
      ],
    }),

    // Delete session (was deleteGym)
    deleteSession: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (sessionid) => ({
        url: `${ApiEndpoints.deleteSession}/${sessionid}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Session", id: "LIST" }],
    }),

    //for session format Create and Get
    createSessionFormat: builder.mutation<
      ApiResponse<any>,
      { sessionid: string; formats: { format: string; order: number }[] }
    >({
      query: ({ sessionid, formats }) => ({
        url: `${ApiEndpoints.createSessionFormat}/${sessionid}`,
        method: "POST",
        body: { formats },
      }),
      invalidatesTags: (_result, _error, { sessionid }) => [
        { type: "Session", id: sessionid },
        "SessionFormats",
      ],
    }),
    getSessionFormat: builder.query<any, string>({
      query: (sessionid) => ({
        url: `${ApiEndpoints.getSessionFormats}/${sessionid}`,
        method: "GET",
      }),
      providesTags: ["SessionFormats"],
    }),
    updateSessionFormat: builder.mutation<
      ApiResponse<any>,
      { sessionid: string; formats: { format: string; order: number }[] }
    >({
      query: ({ sessionid, formats }) => ({
        url: `${ApiEndpoints.updateSessionFormat}/${sessionid}`,
        method: "PATCH",
        body: { formats },
      }),
      invalidatesTags: (_result, _error, { sessionid }) => [
        { type: "Session", id: sessionid },
        "SessionFormats",
      ],
    }),

    // Create Session Exercise for Beginner Level
    createSessionFormatBlock: builder.mutation<
      ApiResponse<any>,
      {
        sessionFormatId: string;
        workouts: { workout: string; order: number; difficulty: string }[];
      }
    >({
      query: ({ sessionFormatId, workouts }) => ({
        url: `${ApiEndpoints.createSessionFormatBlock}/${sessionFormatId}`,
        method: "POST",
        body: { workouts },
      }),
      invalidatesTags: (_result, _error, { sessionFormatId }) => [
        { type: "Session", id: sessionFormatId },
        "SessionFormatBlocks",
      ],
    }),
    // Create Session Format Block for Advanced Level
    getSessionFormatBloacksBySessionFormatId: builder.query<
      any,
      {
        sessionFormatId: string;
        difficulty?: "BEGINNER" | "ADVANCED" | "GENERAL";
      }
    >({
      query: ({ sessionFormatId, difficulty }) => ({
        url: `${ApiEndpoints.getSessionFormatBlocksBySessionFormatId
          }/${sessionFormatId}/${difficulty ? `?difficulty=${encodeURIComponent(difficulty)}` : ""
          }`,
        method: "GET",
      }),
      providesTags: ["SessionFormatBlocks"],
    }),

    createSessionMatriceBlocks: builder.mutation<
      ApiResponse<any>,
      {
        sessionFormatId: string;
        difficulty?: "BEGINNER" | "ADVANCED" | "GENERAL";
        blocks: {
          id: string; // block id (was _id)
          restTime?: number | string;
          workouts: {
            id: string; // workout id (was workout)
            duration: number | string;
            metrics: { metric: string; value: number }[];
            workPeriod: number | string;
            restPeriod: number | string;
            blockTiming: number | string;
          }[];
        }[];
      }
    >({
      query: ({ sessionFormatId, difficulty, blocks }) => {
        const normalizedBlocks = blocks.map((b) => ({
          id: b.id,
          restTime:
            typeof b.restTime === "string"
              ? toSeconds(b.restTime)
              : b.restTime ?? undefined,
          workouts: b.workouts.map((w) => ({
            id: w.id,
            duration:
              typeof w.duration === "string"
                ? toSeconds(w.duration)
                : w.duration,
            metrics: w.metrics.map((m) => ({
              metric: m.metric,
              value: Number(m.value) || 0,
            })),
            workPeriod:
              typeof w.workPeriod === "string"
                ? toSeconds(w.workPeriod)
                : w.workPeriod,
            restPeriod:
              typeof w.restPeriod === "string"
                ? toSeconds(w.restPeriod)
                : w.restPeriod,
            blockTiming:
              typeof w.blockTiming === "string"
                ? toSeconds(w.blockTiming)
                : w.blockTiming,
          })),
        }));
        const difficultyNormalized: "BEGINNER" | "ADVANCED" | "GENERAL" =
          (difficulty as any) ?? "GENERAL";

        return {
          url: `${ApiEndpoints.createSessionMatrixBlocks}/${sessionFormatId}`,
          method: "PATCH",
          body: { difficulty: difficultyNormalized, blocks: normalizedBlocks },
        };
      },
      invalidatesTags: ["SessionFormatBlocks"],
    }),

    createExerciseBlock: builder.mutation<
      ApiResponse<any>,
      {
        sessionFormatId: string;
        workout: string;
        difficulty?: "BEGINNER" | "ADVANCED" | "GENERAL";
      }
    >({
      query: ({ sessionFormatId, workout, difficulty }) => ({
        url: `${ApiEndpoints.createFormatBlock}`,
        method: "POST",
        body: {
          format: sessionFormatId,
          workout,
          difficulty: (difficulty as any) ?? "GENERAL",
        },
      }),
      invalidatesTags: (_result, _error, { sessionFormatId }) => [
        { type: "Session", id: sessionFormatId },
        "SessionFormatBlocks",
      ],
    }),
    deleteSessionMatriceBlock: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { blockId: string }
    >({
      query: ({ blockId }) => ({
        url: `${ApiEndpoints.sessionFormatBlock}/${blockId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SessionFormatBlocks"],
    }),
    deleteSessionMatriceGroupBlock: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { groupId: string; workoutId: string }
    >({
      query: ({ groupId, workoutId }) => ({
        url: `${ApiEndpoints.deleteSessionGropuBlockWorkout}/${groupId}/${workoutId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SessionFormatBlocks"],
    }),

    //for session group creation (selection payload)
    createSessionGroup: builder.mutation<
      ApiResponse<any>,
      {
        sessionFormatId: string;
        groups: {
          blocks: string[];
          group: string;
          difficulty: "BEGINNER" | "ADVANCED" | "GENERAL";
          metrics: { metric: string; value: number; static: boolean }[];
          restTime: number | string;
        }[];
      }
    >({
      query: ({ sessionFormatId, groups }) => {
        return {
          url: `${ApiEndpoints.createSessionGroup}/${sessionFormatId}`,
          method: "POST",
          body: groups,
        };
      },
      invalidatesTags: ["SessionFormatBlocks"],
    }),
    // publish session status
    publishSessionStatusChange: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { sessionid: string; }
    >({
      query: ({ sessionid }) => ({
        url: `${ApiEndpoints.changeSessionStatus}/${sessionid}`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { sessionid }) => [
        { type: "Session", id: sessionid },
        { type: "Session", id: "LIST" },
      ],

    }),
    getSessionSummaryBySessionId: builder.query<
      ApiResponse<any>,
      { sessionid: string; limit?: number; page?: number }
    >({
      query: ({ sessionid, limit = 100, page = 1 }) => ({
        url: `${ApiEndpoints.getSessionSummaryBySessionId}/${sessionid}`,
        method: "GET",
        params: {
          limit,
          page,
        },
      }),
    }),
  }),


});

export const {
  useGetSessionsQuery,
  useSearchSessionsQuery,
  useGetSessionByIdQuery,
  useCreateSessionMutation,
  useDuplicateSessionMutation,
  useEditSessionMutation,
  useDeleteSessionMutation,
  useCreateSessionFormatMutation,
  useGetSessionFormatQuery,
  useUpdateSessionFormatMutation,
  useCreateSessionFormatBlockMutation,
  useGetSessionFormatBloacksBySessionFormatIdQuery,
  useCreateSessionMatriceBlocksMutation,
  useCreateExerciseBlockMutation,
  useDeleteSessionMatriceBlockMutation,
  useDeleteSessionMatriceGroupBlockMutation,
  useCreateSessionGroupMutation,
  usePublishSessionStatusChangeMutation,
  useGetSessionSummaryBySessionIdQuery,
} = workoutSessionApi;
